import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toUnitCode, UNIT_CODE_MAX } from '@xiltrion/entidad';
import type { UnitCheckResult, UnitsView, UnitView } from '@suite/contracts';
import { EstadoCargando, EstadoError, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { SelectorPersonas, type Persona } from '@xiltrion/ui/bloques/personas';
import { SelectorBuscable } from '@xiltrion/ui/bloques/selectores';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { avisar } from '@xiltrion/ui/sonner';
import { Switch } from '@xiltrion/ui/switch';
import { Textarea } from '@xiltrion/ui/textarea';
import { Tree, TreeItem, TreeItemLabel, useTree, hotkeysCoreFeature, selectionFeature, syncDataLoaderFeature } from '@xiltrion/ui/tree';
import { cn } from '@xiltrion/ui/utils';
import { ApiError, apiGet, apiPatch, apiPost, apiPut, errorMessage } from '@/api/client';
import type { MemberRow } from '@/api/types';

const ROOT = 'raiz';
const INDENT = 20;
const CODE_HINT = 'Mayúsculas, números, punto, guion y guion bajo. No se puede cambiar después: SGDEA lo usa para clasificar y el ERP lo lleva en sus registros.';

/** Un cambio que las apps tienen que revisar: se reintenta aceptando sus avisos. */
type Pending = { title: string; result: UnitCheckResult; retry: () => Promise<unknown> };

/**
 * Organigrama de la entidad (GUIA-AGENTES.md §3.15): dependencias, su jefe y sus personas, compartidos con todas las
 * apps. Archivar, borrar, mover o sacar personas se le consulta antes a cada app que ya lo usa: lo que la rompería no
 * se aplica y sus avisos se confirman aquí.
 */
export function EntityUnitsTab({ slug, members }: { slug: string; members: MemberRow[] }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['entity-units', slug], queryFn: () => apiGet<UnitsView>(`/entities/${slug}/units`) });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState<{ parentId: string | null } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [blocked, setBlocked] = useState<{ title: string; result: UnitCheckResult } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['entity-units', slug] });

  /** Corre un cambio vigilado: con bloqueos los muestra; con avisos pide confirmarlos y lo repite aceptándolos. */
  const guarded = async (title: string, run: (acceptWarnings: boolean) => Promise<unknown>, done: string) => {
    try {
      await run(false);
      avisar.success(done);
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'UNIT_CHANGE_WARNINGS') setPending({ title, result: e.details as UnitCheckResult, retry: () => run(true).then(async () => { avisar.success(done); await refresh(); }) });
      else if (e instanceof ApiError && e.code === 'UNIT_CHANGE_BLOCKED') setBlocked({ title, result: e.details as UnitCheckResult });
      else avisar.error(errorMessage(e));
    }
  };

  if (q.error) return <EstadoError error={q.error} />;
  if (!q.data) return <EstadoCargando />;
  const { units } = q.data;
  const visible = showArchived ? units : units.filter((u) => u.status === 'ACTIVE');
  const selected = units.find((u) => u.id === selectedId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-4">
        {units.some((u) => u.status === 'ARCHIVED') && <label className="flex items-center gap-2 text-sm"><Switch checked={showArchived} onCheckedChange={setShowArchived} />Ver archivadas</label>}
        <Button onClick={() => setCreating({ parentId: null })}><Icono nombre="mdi:plus" />Nueva dependencia</Button>
      </div>
      {units.length === 0 ? (
        <EstadoVacio titulo="Sin dependencias" descripcion="Crea las dependencias de la entidad (secretarías, oficinas, áreas) y quién trabaja en cada una. Las apps las usan para repartir el trabajo." />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
          <Card className="@container min-w-0">
            <CardContent className="p-2">
              <UnitsTree units={visible} selectedId={selectedId} onSelect={setSelectedId} onCreate={(parentId) => setCreating({ parentId })} />
            </CardContent>
          </Card>
          {selected ? (
            <UnitPanel key={selected.id} slug={slug} unit={selected} units={units} members={members} guarded={guarded} onDeleted={() => setSelectedId(null)} />
          ) : (
            <Card><CardContent className="py-10"><EstadoVacio titulo="Elige una dependencia" descripcion="Para cambiar su nombre, su jefe, de quién depende o sus personas." /></CardContent></Card>
          )}
        </div>
      )}
      {creating && <CreateUnitDialog slug={slug} units={units.filter((u) => u.status === 'ACTIVE')} members={members} parentId={creating.parentId} onClose={() => setCreating(null)} onCreated={async (id) => { setCreating(null); await refresh(); setSelectedId(id); }} />}
      <ConfirmarDialogo
        abierto={!!pending}
        onAbiertoChange={(v) => { if (!v) setPending(null); }}
        titulo={pending?.title ?? ''}
        descripcion="Las apps avisan esto. Si sigues, el cambio se aplica en todas."
        textoConfirmar="Aplicar de todas formas"
        tono="warning"
        icono="mdi:alert-outline"
        onConfirmar={async () => { try { await pending!.retry(); setPending(null); } catch (e) { setPending(null); if (e instanceof ApiError && e.code === 'UNIT_CHANGE_BLOCKED') setBlocked({ title: pending!.title, result: e.details as UnitCheckResult }); else avisar.error(errorMessage(e)); } }}
      >
        {pending && <AppIssues result={pending.result} kind="warnings" />}
      </ConfirmarDialogo>
      <Dialog open={!!blocked} onOpenChange={(v) => { if (!v) setBlocked(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{blocked?.title}</DialogTitle><DialogDescription>No se aplicó: una app no lo permite o no respondió.</DialogDescription></DialogHeader>
          {blocked && <AppIssues result={blocked.result} kind="blockers" />}
          <DialogFooter><Button variant="outline" onClick={() => setBlocked(null)}>Entendido</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Lo que dijo cada app: bloqueos (o que no respondió) o avisos. */
function AppIssues({ result, kind }: { result: UnitCheckResult; kind: 'blockers' | 'warnings' }) {
  const rows = result.apps.filter((a) => (kind === 'blockers' ? a.status === 'unavailable' || a.blockers.length : a.warnings.length));
  return (
    <div className="space-y-3">
      {rows.map((a) => (
        <Alert key={a.appCode} variant={kind === 'blockers' ? 'destructive' : 'warning'}>
          <Icono nombre={a.status === 'unavailable' ? 'mdi:lan-disconnect' : kind === 'blockers' ? 'mdi:block-helper' : 'mdi:alert-outline'} />
          <AlertTitle>{a.appName}</AlertTitle>
          <AlertDescription>
            {a.status === 'unavailable' ? 'No respondió. Inténtalo de nuevo en un momento.' : (
              <ul className="list-disc pl-4">{(kind === 'blockers' ? a.blockers : a.warnings).map((i) => <li key={i.code + i.message}>{i.message}</li>)}</ul>
            )}
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
}

type TreeNode = { kind: 'root'; name: string; children: string[] } | { kind: 'unit'; name: string; children: string[]; unit: UnitView };

/** Árbol de dependencias sobre `Tree` de @xiltrion/ui (el mismo patrón que `OrgTree` de SGDEA). */
function UnitsTree({ units, selectedId, onSelect, onCreate }: { units: UnitView[]; selectedId: string | null; onSelect: (id: string) => void; onCreate: (parentId: string) => void }) {
  const nodes = useMemo(() => {
    const m: Record<string, TreeNode> = {};
    const ids = new Set(units.map((u) => u.id));
    const childrenOf = (parentId: string | null) => units.filter((u) => (parentId ? u.parentId === parentId : !u.parentId || !ids.has(u.parentId)));
    const add = (u: UnitView): string => {
      m[u.id] = { kind: 'unit', name: u.name, unit: u, children: childrenOf(u.id).map(add) };
      return u.id;
    };
    m[ROOT] = { kind: 'root', name: 'Entidad', children: childrenOf(null).map(add) };
    return m;
  }, [units]);
  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  const folders = useMemo(() => Object.keys(nodes).filter((k) => k !== ROOT && nodes[k]!.children.length > 0), [nodes]);
  const expandedItems = useMemo(() => folders.filter((k) => !closed.has(k)), [folders, closed]);
  const selectedItems = useMemo(() => (selectedId ? [selectedId] : []), [selectedId]);
  const tree = useTree<TreeNode>({
    rootItemId: ROOT,
    indent: INDENT,
    state: { expandedItems, selectedItems },
    setExpandedItems: (next) => {
      const open = new Set(typeof next === 'function' ? next(expandedItems) : next);
      setClosed(new Set(folders.filter((k) => !open.has(k))));
    },
    setSelectedItems: () => { /* la selección la manda `selectedId` */ },
    onPrimaryAction: (item) => {
      const n = item.getItemData();
      if (n.kind === 'unit') onSelect(n.unit.id);
    },
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => item.getItemData().children.length > 0,
    dataLoader: { getItem: (id) => nodes[id]!, getChildren: (id) => nodes[id]?.children ?? [] },
    features: [syncDataLoaderFeature, selectionFeature, hotkeysCoreFeature],
  });
  // Headless Tree guarda su propia lista de filas: se rehace cuando cambian los datos.
  useEffect(() => { tree.rebuildTree(); }, [tree, nodes]);

  return (
    <Tree tree={tree} indent={INDENT} lineas aria-label="Dependencias" className="[--tree-fondo:var(--card)]">
      {tree.getItems().map((item) => {
        const n = item.getItemData();
        if (!n || n.kind === 'root') return null;
        const u = n.unit;
        const archived = u.status === 'ARCHIVED';
        const pick = (e: React.MouseEvent) => { e.stopPropagation(); item.setFocused(); item.primaryAction(); };
        return (
          <TreeItem key={item.getId()} item={item} asChild>
            <div>
              <TreeItemLabel>
                <span className="flex min-w-0 flex-1 cursor-pointer items-center gap-2" onClick={pick}>
                  <Icono nombre="mdi:office-building-outline" className="size-4 shrink-0 text-muted-foreground" />
                  <span className={cn('min-w-[7rem] max-w-[60%] shrink-0 truncate', archived && 'text-muted-foreground line-through')} title={u.name}>{u.name}</span>
                  <span className="min-w-0 shrink-[6] truncate text-xs text-muted-foreground">{u.code}</span>
                  {archived && <Badge variant="secondary">Archivada</Badge>}
                  <span className="ml-auto hidden min-w-0 items-center gap-3 pr-1 text-xs text-muted-foreground @md:flex">
                    {u.manager && <span className="hidden truncate @2xl:inline">Jefe: {u.manager.displayName}</span>}
                    <span className="shrink-0 tnum">{u.members.length} persona{u.members.length === 1 ? '' : 's'}</span>
                  </span>
                  {!archived && (
                    <span className="ml-auto shrink-0 @md:ml-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon-sm" aria-label={`Nueva dependencia dentro de ${u.name}`} onClick={() => onCreate(u.id)}><Icono nombre="mdi:plus" /></Button>
                    </span>
                  )}
                </span>
              </TreeItemLabel>
            </div>
          </TreeItem>
        );
      })}
    </Tree>
  );
}

const personOf = (m: MemberRow, id: string): Persona => ({ id, nombre: m.displayName, correo: m.email, detalle: m.jobTitle ?? undefined });

/** Opciones de «Depende de»: las activas, sin la dependencia misma ni las que tiene adentro. */
function parentOptions(units: UnitView[], selfId?: string) {
  const inside = new Set<string>(selfId ? [selfId] : []);
  for (let grew = true; grew;) {
    grew = false;
    for (const u of units) if (u.parentId && inside.has(u.parentId) && !inside.has(u.id)) { inside.add(u.id); grew = true; }
  }
  return units.filter((u) => u.status === 'ACTIVE' && !inside.has(u.id)).map((u) => ({ value: u.id, label: u.name, descripcion: u.code }));
}

function UnitPanel({ slug, unit, units, members, guarded, onDeleted }: { slug: string; unit: UnitView; units: UnitView[]; members: MemberRow[]; guarded: (title: string, run: (acceptWarnings: boolean) => Promise<unknown>, done: string) => Promise<void>; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: unit.name, description: unit.description ?? '', managerUserId: unit.manager?.userId ?? null, parentId: unit.parentId });
  const [people, setPeople] = useState(unit.members.map((m) => m.membershipId));
  const [confirm, setConfirm] = useState<'archive' | 'delete' | null>(null);
  const [saving, setSaving] = useState(false);
  const active = members.filter((m) => m.status === 'ACTIVE');
  // Quien ya está en la dependencia sigue en la lista aunque esté inactivo, para poder sacarlo.
  const peopleOptions = members.filter((m) => m.status === 'ACTIVE' || unit.members.some((x) => x.membershipId === m.id)).map((m) => personOf(m, m.id));
  const archived = unit.status === 'ARCHIVED';
  const dirty = form.name !== unit.name || form.description !== (unit.description ?? '') || form.managerUserId !== (unit.manager?.userId ?? null) || form.parentId !== unit.parentId;
  const peopleDirty = [...people].sort().join() !== unit.members.map((m) => m.membershipId).sort().join();

  const save = async () => {
    setSaving(true);
    try {
      const moving = form.parentId !== unit.parentId;
      await guarded(moving ? `Mover «${unit.name}»` : `Guardar «${unit.name}»`, (acceptWarnings) => apiPatch(`/entities/${slug}/units/${unit.id}`, { name: form.name, description: form.description || null, managerUserId: form.managerUserId, ...(moving ? { parentId: form.parentId } : {}), acceptWarnings }), 'Dependencia guardada. Las apps la reciben al momento.');
    } finally {
      setSaving(false);
    }
  };
  const savePeople = async () => {
    setSaving(true);
    try {
      await guarded(`Personas de «${unit.name}»`, (acceptWarnings) => apiPut(`/entities/${slug}/units/${unit.id}/members`, { membershipIds: people, acceptWarnings }), 'Personas guardadas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="min-w-0 self-start">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">{unit.name}{archived && <Badge variant="secondary">Archivada</Badge>}</CardTitle>
        <CardDescription className="font-mono">{unit.code}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FieldGroup>
          <Field><FieldLabel htmlFor="u-name">Nombre</FieldLabel><Input id="u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field>
            <FieldLabel htmlFor="u-desc">Qué atiende</FieldLabel>
            <Textarea id="u-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <FieldDescription>SGDEA lo lee para clasificar las solicitudes: sin esto, no le llegan.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel>Depende de</FieldLabel>
            <SelectorBuscable value={form.parentId} onChange={(v) => setForm({ ...form, parentId: v })} options={parentOptions(units, unit.id)} limpiable textoSinValor="Ninguna (primer nivel)" placeholder="Ninguna (primer nivel)" aria-label="Depende de" />
          </Field>
          <Field>
            <FieldLabel>Jefe</FieldLabel>
            <SelectorPersonas personas={active.map((m) => personOf(m, m.userId))} valor={form.managerUserId} onCambiar={(v) => setForm({ ...form, managerUserId: v })} permitirVacio placeholder="Sin jefe" aria-label="Jefe" />
            <FieldDescription>Cualquier persona de la entidad. A quien escalan las apps.</FieldDescription>
          </Field>
        </FieldGroup>
        <div className="flex justify-end"><Button disabled={!dirty || !form.name.trim() || saving} onClick={() => void save()}>Guardar</Button></div>

        <Field>
          <FieldLabel>Personas</FieldLabel>
          <SelectorPersonas multiple personas={peopleOptions} valor={people} onCambiar={setPeople} placeholder="Agregar personas" aria-label="Personas de la dependencia" />
          <FieldDescription>{people.length} persona{people.length === 1 ? '' : 's'}. Una persona puede estar en varias dependencias.</FieldDescription>
        </Field>
        {peopleDirty && <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setPeople(unit.members.map((m) => m.membershipId))}>Descartar</Button><Button disabled={saving} onClick={() => void savePeople()}>Guardar personas</Button></div>}

        <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
          {archived
            ? <Button variant="outline" onClick={() => void apiPost(`/entities/${slug}/units/${unit.id}/restore`).then(async () => { avisar.success('Dependencia reactivada'); await qc.invalidateQueries({ queryKey: ['entity-units', slug] }); }, (e: unknown) => avisar.error(errorMessage(e)))}><Icono nombre="mdi:archive-arrow-up-outline" />Reactivar</Button>
            : <Button variant="outline" onClick={() => setConfirm('archive')}><Icono nombre="mdi:archive-outline" />Archivar</Button>}
          <Button variant="ghost" className="text-destructive" onClick={() => setConfirm('delete')}><Icono nombre="mdi:delete-outline" />Borrar</Button>
        </div>
      </CardContent>
      <ConfirmarDialogo
        abierto={!!confirm}
        onAbiertoChange={(v) => { if (!v) setConfirm(null); }}
        titulo={confirm === 'delete' ? `Borrar «${unit.name}»` : `Archivar «${unit.name}»`}
        descripcion={confirm === 'delete'
          ? 'Solo para una dependencia creada de más: se borra si ninguna app la usó, y su código queda libre. Si ya se usó, archívala.'
          : 'Sale de las listas de las apps, pero sus registros la conservan. Puedes reactivarla cuando quieras.'}
        textoConfirmar={confirm === 'delete' ? 'Borrar' : 'Archivar'}
        destructivo={confirm === 'delete'}
        tono={confirm === 'delete' ? undefined : 'warning'}
        icono={confirm === 'delete' ? 'mdi:delete-outline' : 'mdi:archive-outline'}
        onConfirmar={async () => {
          const kind = confirm;
          setConfirm(null);
          if (kind === 'delete') await guarded(`Borrar «${unit.name}»`, (acceptWarnings) => apiPost(`/entities/${slug}/units/${unit.id}/delete`, { acceptWarnings }).then(onDeleted), 'Dependencia borrada');
          else await guarded(`Archivar «${unit.name}»`, (acceptWarnings) => apiPost(`/entities/${slug}/units/${unit.id}/archive`, { acceptWarnings }), 'Dependencia archivada');
        }}
      />
    </Card>
  );
}

function CreateUnitDialog({ slug, units, members, parentId, onClose, onCreated }: { slug: string; units: UnitView[]; members: MemberRow[]; parentId: string | null; onClose: () => void; onCreated: (id: string) => Promise<void> }) {
  const [form, setForm] = useState({ name: '', code: '', description: '', parentId, managerUserId: null as string | null });
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const code = codeTouched ? form.code : toUnitCode(form.name);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await apiPost<{ id: string }>(`/entities/${slug}/units`, { name: form.name, code: toUnitCode(code), description: form.description || null, parentId: form.parentId, managerUserId: form.managerUserId });
      avisar.success('Dependencia creada');
      await onCreated(r.id);
    } catch (e) {
      avisar.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Nueva dependencia</DialogTitle><DialogDescription>Secretaría, oficina, área o grupo de trabajo. Las personas se agregan después.</DialogDescription></DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <FieldGroup>
            <Field><FieldLabel htmlFor="nu-name">Nombre</FieldLabel><Input id="nu-name" required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field>
              <FieldLabel htmlFor="nu-code">Código</FieldLabel>
              <Input id="nu-code" required className="font-mono" maxLength={UNIT_CODE_MAX} value={code} onChange={(e) => { setCodeTouched(true); setForm({ ...form, code: toUnitCode(e.target.value, { keepTrailing: true }) }); }} />
              <FieldDescription>{CODE_HINT}</FieldDescription>
            </Field>
            <Field><FieldLabel htmlFor="nu-desc">Qué atiende</FieldLabel><Textarea id="nu-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field><FieldLabel>Depende de</FieldLabel><SelectorBuscable value={form.parentId} onChange={(v) => setForm({ ...form, parentId: v })} options={parentOptions(units)} limpiable textoSinValor="Ninguna (primer nivel)" placeholder="Ninguna (primer nivel)" aria-label="Depende de" /></Field>
            <Field><FieldLabel>Jefe</FieldLabel><SelectorPersonas personas={members.filter((m) => m.status === 'ACTIVE').map((m) => personOf(m, m.userId))} valor={form.managerUserId} onCambiar={(v) => setForm({ ...form, managerUserId: v })} permitirVacio placeholder="Sin jefe" aria-label="Jefe" /></Field>
          </FieldGroup>
          <DialogFooter><Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button><Button type="submit" disabled={saving || !form.name.trim() || !toUnitCode(code)}>Crear</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
